// The audit trail (migration 0008), read for the History screens. Row-level security decides what each
// person sees: administrators and auditors see everything, a department sees only rows stamped with its
// own department. Nothing here filters for security; the filters are only what the person asked for.
import { supabase } from '@/lib/supabase'
import { describeChanges, kindOf, OP_VERBS, recordLabel } from '@/lib/auditText'
import type { AuditOp, FieldChange } from '@/lib/auditText'
import { unwrap } from './errors'

export const HISTORY_PAGE = 50

export interface HistoryFilter {
  departmentId?: string
  table?: string
  actorId?: string
  /** Lusaka business dates, 'YYYY-MM-DD', both inclusive. */
  from?: string
  to?: string
}

export interface HistoryEntry {
  id: number
  at: string
  /** The person's name, or their email if their account cannot be read, or "The system". */
  who: string
  table: string
  kind: string
  op: AuditOp
  verb: string
  departmentId: string | null
  /** A short name for the record ('Mary Banda', 'Fuel, K120.00'), when one can be worked out. */
  label: string | null
  changes: FieldChange[]
}

export interface HistoryPage {
  entries: HistoryEntry[]
  /** Pass back as `before` to read the next (older) page; null when there is nothing older. */
  next: number | null
}

type Row = {
  id: number
  occurred_at: string
  actor_id: string | null
  actor_email: string | null
  table_name: string
  row_id: string | null
  department_id: string | null
  op: AuditOp
  changes: Record<string, unknown>
}

/** The start of a Lusaka day as a moment (Lusaka is UTC+2 all year, no daylight saving). */
const lusakaMidnight = (day: string) => `${day}T00:00:00+02:00`
const nextDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
}

/** One page of the history, newest first. */
export async function listHistory(filter: HistoryFilter = {}, before?: number): Promise<HistoryPage> {
  let q = supabase
    .from('audit_log')
    .select('id, occurred_at, actor_id, actor_email, table_name, row_id, department_id, op, changes')
    .order('id', { ascending: false })
    .limit(HISTORY_PAGE + 1)
  if (before !== undefined) q = q.lt('id', before)
  if (filter.departmentId) q = q.eq('department_id', filter.departmentId)
  if (filter.table) q = q.eq('table_name', filter.table)
  if (filter.actorId) q = q.eq('actor_id', filter.actorId)
  if (filter.from) q = q.gte('occurred_at', lusakaMidnight(filter.from))
  if (filter.to) q = q.lt('occurred_at', lusakaMidnight(nextDay(filter.to)))
  const rows = unwrap(await q, 'Could not load the history.') as unknown as Row[]
  const page = rows.slice(0, HISTORY_PAGE)

  // Names for the records: a change keeps only what changed, so look up how each record looked when it
  // was added. Same row-level security, so this reveals nothing the trail itself does not.
  const needLabel = [...new Set(page.filter((r) => r.op !== 'INSERT' && r.row_id).map((r) => r.row_id as string))]
  const firstSeen = new Map<string, Record<string, unknown>>()
  if (needLabel.length) {
    const res = await supabase.from('audit_log').select('row_id, changes').eq('op', 'INSERT').in('row_id', needLabel)
    for (const r of unwrap(res, 'Could not load the history.') as unknown as { row_id: string; changes: Record<string, unknown> }[]) {
      firstSeen.set(r.row_id, r.changes)
    }
  }

  // Names for the people. Accounts a person may not read just show the email the trail recorded.
  const actorIds = [...new Set(page.map((r) => r.actor_id).filter((x): x is string => x !== null))]
  const names = new Map<string, string>()
  if (actorIds.length) {
    const res = await supabase.from('app_user').select('id, full_name').in('id', actorIds)
    for (const u of unwrap(res, 'Could not load the history.') as { id: string; full_name: string }[]) names.set(u.id, u.full_name)
  }

  return {
    entries: page.map((r) => {
      // A change to a record keeps only what changed; merge it over how it looked at first for the name.
      const after = r.op === 'UPDATE' && r.row_id ? { ...firstSeen.get(r.row_id), ...currentValues(r.changes) } : null
      return {
        id: r.id,
        at: r.occurred_at,
        who: (r.actor_id && names.get(r.actor_id)) || r.actor_email || 'The system',
        table: r.table_name,
        kind: kindOf(r.table_name),
        op: r.op,
        verb: OP_VERBS[r.op],
        departmentId: r.department_id,
        label: recordLabel(r.table_name, r.op === 'UPDATE' ? after : r.changes),
        changes: describeChanges(r.op, r.changes),
      }
    }),
    next: rows.length > HISTORY_PAGE ? page[page.length - 1].id : null,
  }
}

/** {col: [old, new]} -> {col: new} */
function currentValues(changes: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(changes ?? {}).map(([k, v]) => [k, Array.isArray(v) ? v[1] : v]))
}
